import { inject as service } from '@ember/service';
import { task } from 'ember-concurrency';
import Controller from '@ember/controller';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import AlertsService from 'cio/services/alerts';
import DeliveryService from 'cio/services/delivery';
import RouterService from '@ember/routing/router-service';
import DeliveryModel from 'cio/models/delivery';
import DeliveryDetailModel from 'cio/models/delivery-detail';
import AuthService from 'cio/services/auth';

export default class BaseDraftController extends Controller {
  @service auth!: AuthService;
  @service alerts!: AlertsService;
  @service('delivery') deliveryService!: DeliveryService;
  @service router!: RouterService;

  declare model: DeliveryModel;

  @tracked isNewsletterDelivery = false;
  @tracked isBroadcastDelivery = false;
  @tracked isCampaignDelivery = false;
  @tracked isTransactionalDelivery = false;

  constructor() {
    super(...arguments);

    this.fetchDeliveryRelationships.perform();
  }

  fetchDeliveryRelationships = task({ drop: true }, async () => {
    if (this.model != null) {
      if (this.model.newsletterId != null) {
        this.isNewsletterDelivery = true;
      } else if (this.model.transactionalMessageId != null) {
        this.isTransactionalDelivery = true;
      } else if (this.model.campaignId != null) {
        let campaign = await this.model.belongsTo('campaign').load();

        if (campaign.isApiTriggered === true) {
          this.isBroadcastDelivery = true;
        } else {
          this.isCampaignDelivery = true;
        }
      }
    }
  });

  get hasReadOnlyPermission() {
    if (this.isNewsletterDelivery) {
      return !this.auth.hasPermission('journeys/newsletters/edit');
    }

    if (this.isTransactionalDelivery) {
      return !this.auth.hasPermission('journeys/transactional_messages/edit');
    }

    if (this.isBroadcastDelivery) {
      return !this.auth.hasPermission('journeys/broadcasts/edit');
    }

    if (this.isCampaignDelivery) {
      return !this.auth.hasPermission('journeys/campaigns/edit');
    }

    return;
  }

  get deliveryDetail() {
    return this.model.belongsTo('detail').value() as DeliveryDetailModel;
  }

  @action
  async onSend() {
    let delivery = this.model;
    let detail = this.deliveryDetail;

    if (detail == null) {
      return;
    }

    this.alerts.remove('draft.send-error');
    this.alerts.sticky('draft.sending', 'Sending draft…');

    let data = {
      overrides: {
        sender_identity_id: detail.fromIdentity?.id,
        title: detail.subject,
        body: detail.body,
        bcc: detail.bcc,
        recipient: detail.recipient,
        reply_to_identity_id: detail.replyToIdentity?.id,
        preheader_text: detail.preheaderText,
      },
    };

    try {
      await this.deliveryService.deliver(delivery.id, data);
      let message = `Your draft ${data.overrides.title} will be sent soon`;

      this.alerts.success('draft.sent', message);
      delivery.rollbackAttributes();
      detail.rollbackAttributes();

      delivery.set('pendingSend', true);
      let campaign = await delivery.belongsTo('campaign').load();

      if (campaign != null) {
        this.router.transitionTo('campaigns.campaign', campaign.id);
      }
    } catch {
      this.alerts.error('draft.send-error', "We weren't able to send your draft");
    } finally {
      this.alerts.remove('draft.sending');
    }
  }

  @action
  onCancel() {
    this.model.rollbackAttributes();
    this.deliveryDetail?.rollbackAttributes();
    this.router.transitionTo('draft.index');
  }
}
